import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { request } from '../../api/client';
import { Modal, Form, useForm, Input, Row, Col, Button, Switch, message } from '../../ui';
import { PhoneInput } from '../../components/PhoneInput';
import { mobileRules, gstinRules } from '../../lib/validators';

/**
 * ADD / EDIT A TRANSPORTER — one form, two callers.
 *
 * The master page opens it in full. The pack screen opens it in `quick` mode, where a warehouse
 * manager standing at the dock types a name and a phone number and gets back a transporter they can
 * select immediately: the whole point of a mandatory dropdown is that it must never be the reason a
 * lorry waits. Both go through the same POST /transporters, so the quick path cannot skip validation
 * or the audit row — it simply asks fewer questions, and an admin fills in the rest later.
 */
export interface TransporterRecord {
  id: string;
  name: string;
  contactPerson?: string | null;
  mobile?: string | null;
  email?: string | null;
  gstNo?: string | null;
  city?: string | null;
  notes?: string | null;
  isActive?: boolean;
  /** From BaseEntity — shown as "Added" on the master list. */
  createdAt?: string | null;
}

export function TransporterFormModal({
  open, record, quick = false, onClose, onCreated,
}: {
  open: boolean;
  /** Present = edit that transporter; absent = add a new one. */
  record?: TransporterRecord | null;
  /** Pack-screen mode: name + who to call, nothing else. */
  quick?: boolean;
  onClose: () => void;
  /** Handed the saved row, so the pack screen can select what was just added. */
  onCreated?: (t: TransporterRecord) => void;
}) {
  const [form] = useForm();
  const qc = useQueryClient();
  const isEdit = !!record;

  const save = useConfirmedMutation({ title: "Save this transporter?", content: "Packing staff pick transporters from this list.", okText: "Yes, save" }, {
    mutationFn: (vals: any) => request<TransporterRecord>({
      url: isEdit ? `/transporters/${record!.id}` : '/transporters',
      method: isEdit ? 'PUT' : 'POST',
      data: {
        name: vals.name,
        contactPerson: vals.contactPerson || null,
        mobile: vals.mobile || null,
        city: vals.city || null,
        ...(quick ? {} : {
          email: vals.email || null,
          gstNo: vals.gstNo || null,
          notes: vals.notes || null,
        }),
        ...(isEdit ? { isActive: !!vals.isActive } : {}),
      },
    }),
    onSuccess: (saved: any) => {
      message.success(isEdit ? 'Transporter updated' : `${saved?.name ?? 'Transporter'} added`);
      qc.invalidateQueries({ queryKey: ['transporters'] });
      if (!isEdit && saved) onCreated?.(saved);
      onClose();
    },
    onError: (e: any) => message.error(
      e.response?.data?.error?.message ?? e.response?.data?.message ?? 'Could not save that transporter'),
  });

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={isEdit ? `Edit ${record?.name}` : 'Add a transporter'}
      description={quick ? 'It is added to the master list and selected for this shipment.' : undefined}
      width={quick ? 520 : 640}
    >
      {/* Seeded via key + initialValues, never setFieldsValue in an effect (that races the remount). */}
      <Form
        key={record?.id ?? 'new'}
        form={form}
        layout="vertical"
        onFinish={(v) => save.mutate(v)}
        initialValues={{
          name: record?.name ?? '',
          contactPerson: record?.contactPerson ?? '',
          mobile: record?.mobile ?? undefined,
          email: record?.email ?? '',
          gstNo: record?.gstNo ?? '',
          city: record?.city ?? '',
          notes: record?.notes ?? '',
          isActive: record ? !!record.isActive : true,
        }}
      >
        <Form.Item
          name="name"
          label="Transporter name"
          rules={[{ required: true, message: 'A transporter name is required' }]}
          extra={isEdit ? 'Renaming updates what every shipment already booked with them displays.' : undefined}
        >
          <Input placeholder="e.g. VRL Logistics" autoFocus />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name="contactPerson" label="Contact person">
              <Input placeholder="Who to ask for" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="mobile" label="Mobile" rules={mobileRules(false)}>
              <PhoneInput placeholder="Mobile number" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={quick ? 24 : 12}>
            <Form.Item name="city" label="City / base">
              <Input placeholder="Where they operate from" />
            </Form.Item>
          </Col>
          {!quick && (
            <Col xs={24} sm={12}>
              <Form.Item name="gstNo" label="GSTIN" rules={gstinRules}>
                <Input placeholder="27AAAAA0000A1Z5" />
              </Form.Item>
            </Col>
          )}
        </Row>

        {!quick && (
          <>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item name="email" label="Email">
                  <Input placeholder="billing@transporter.com" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="notes" label="Notes">
                  <Input placeholder="Rates, routes they cover, anything worth remembering" />
                </Form.Item>
              </Col>
            </Row>
            {isEdit && (
              <Form.Item
                name="isActive"
                label="Available to pick when packing"
                valuePropName="checked"
                extra="Switching this off takes them out of the pack dropdown. Shipments they already carried keep their name."
              >
                <Switch />
              </Form.Item>
            )}
          </>
        )}

        <Button variant="primary" type="submit" block loading={save.isPending}>
          {isEdit ? 'Save transporter' : 'Add transporter'}
        </Button>
      </Form>
    </Modal>
  );
}
